import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client, { imgUrl, logoUrl, API } from "@/lib/glossyApi";
import { Eye, ArrowRight, Check, PenLine } from "lucide-react";
import { ARTICLE_TYPE_LABEL as TYPE_LABEL } from "@/lib/articleTypes";

export default function MemberPreview() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [openInline, setOpenInline] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);

  const respond = async (d) => {
    setSending(true);
    try {
      await client.post(`/preview/${token}/respond`, { decision: d, note });
      setDecision(d);
      setShowNote(false);
    } catch { /* leave buttons usable */ }
    setSending(false);
  };

  useEffect(() => {
    client.get(`/preview/${token}`).then((r) => {
      setData(r.data);
      document.title = `Preview — ${r.data.member.business_name} × S/ME`;
    }).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-8" data-testid="preview-not-found">
        <p className="font-sans text-ink/60">This preview link isn&apos;t live any more. Ask the S/ME desk for a fresh one.</p>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-cream" />;

  const m = data.member;
  return (
    <div className="min-h-screen bg-cream" data-testid="member-preview-page">
      <div className="bg-ink text-cream px-5 py-3 sticky top-0 z-40 flex items-center justify-center gap-3" data-testid="preview-banner">
        <Eye size={14} />
        <p className="font-mono text-[11px] uppercase tracking-widest">Private preview — your S/ME package before it goes live. Only people with this link can see it.</p>
      </div>

      <header className="relative">
        {m.cover_image && (
          <div className="relative h-[46vh] sm:h-[56vh] overflow-hidden bg-ink">
            <img src={`${API}/files/${m.cover_image}`} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
            <img src={`${API}/files/${m.cover_image}`} alt={m.business_name} className="absolute inset-0 w-full h-full object-contain" data-testid="preview-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
          </div>
        )}
        <div className={`max-w-4xl mx-auto px-5 ${m.cover_image ? "-mt-24 relative" : "pt-16"}`}>
          <div className="bg-cream border-2 border-ink p-6 sm:p-8">
            <p className="overline text-ink/50">S/ME × {m.category || "Member"}{m.borough ? ` · ${m.borough}` : ""}</p>
            <h1 className="font-serif text-3xl sm:text-5xl text-ink mt-2" data-testid="preview-business-name">{m.business_name}</h1>
            {m.strapline && <p className="font-sans text-ink/60 mt-2">{m.strapline}</p>}
            {m.logo && <img src={logoUrl(m)} alt="logo" className="h-10 mt-4 object-contain" data-testid="preview-logo" />}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12 space-y-10">
        <div>
          <p className="overline text-ink/50 mb-4">Your features in the magazine — tap any to read it in full</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.articles.map((a) => (
              a.inline_body ? (
                <button key={a.id} onClick={() => setOpenInline(!openInline)} className="group border border-ink/15 bg-white hover:border-ink transition-colors text-left" data-testid={`preview-article-${a.type}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-ink/5">
                    <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <p className="overline text-[10px] text-gloss-gold-dark">Cover Feature · not yet live</p>
                    <h3 className="font-serif text-lg text-ink mt-1 leading-snug">{a.title}</h3>
                    {a.dek && <p className="font-sans text-sm text-ink/55 mt-1 line-clamp-2">{a.dek}</p>}
                    <p className="font-mono text-[10px] text-ink/40 mt-2 flex items-center gap-1">{openInline ? "Close it" : "Read it"} <ArrowRight size={10} /></p>
                  </div>
                </button>
              ) : (
              <Link key={a.id} to={`/read/${a.slug}?pt=${token}`} className="group border border-ink/15 bg-white hover:border-ink transition-colors" data-testid={`preview-article-${a.type}`}>
                <div className="aspect-[16/10] overflow-hidden bg-ink/5">
                  <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <p className="overline text-[10px] text-gloss-gold-dark">{TYPE_LABEL[a.type] || a.category || "Feature"}{a.status !== "published" ? " · not yet live" : " · live"}</p>
                  <h3 className="font-serif text-lg text-ink mt-1 leading-snug">{a.title}</h3>
                  {a.dek && <p className="font-sans text-sm text-ink/55 mt-1 line-clamp-2">{a.dek}</p>}
                  <p className="font-mono text-[10px] text-ink/40 mt-2 flex items-center gap-1">Read it <ArrowRight size={10} /></p>
                </div>
              </Link>
              )
            ))}
            {data.articles.length === 0 && <p className="font-sans text-sm text-ink/50">No features made yet — they&apos;ll appear here as the desk builds them.</p>}
          </div>
          {openInline && (() => {
            const fa = data.articles.find((a) => a.inline_body);
            return fa ? (
              <article className="mt-6 border-2 border-ink bg-white p-6 sm:p-10 max-w-3xl mx-auto" data-testid="preview-inline-feature">
                <p className="overline text-gloss-gold-dark">Cover Feature — preview</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-2 leading-tight">{fa.title}</h2>
                {fa.dek && <p className="font-sans text-lg text-ink/60 mt-3">{fa.dek}</p>}
                <div className="mt-6 space-y-4">
                  {(fa.inline_body || "").split(/\n\n+/).map((p, i) => (
                    p.trim().startsWith(">") ? (
                      <blockquote key={i} className="border-l-4 border-gloss-gold pl-4 font-serif text-xl text-ink/80 italic">{p.replace(/^>\s*/, "")}</blockquote>
                    ) : (
                      <p key={i} className="font-sans text-[15px] leading-relaxed text-ink/80">{p}</p>
                    )
                  ))}
                </div>
              </article>
            ) : null;
          })()}
        </div>

        {m.advert?.headline && (
          <div>
            <p className="overline text-ink/50 mb-4">Your advert</p>
            <div className="border-2 border-ink bg-white p-6 flex flex-col sm:flex-row gap-5 items-center" data-testid="preview-advert">
              {m.advert.image && <img src={`${API}/files/${m.advert.image}`} alt="advert" className="w-full sm:w-56 object-cover" />}
              <div>
                <h3 className="font-serif text-2xl text-ink">{m.advert.headline}</h3>
                {m.advert.body && <p className="font-sans text-sm text-ink/60 mt-2">{m.advert.body}</p>}
              </div>
            </div>
          </div>
        )}

        {data.directory && (
          <div data-testid="preview-directory">
            <p className="overline text-ink/50 mb-4">Your directory card — how readers find you in the S/ME directory</p>
            <div className="max-w-xs border border-ink/15 bg-white">
              <div className="aspect-square bg-secondary border-b border-ink/10 flex items-center justify-center overflow-hidden p-6">
                {data.directory.image && !logoBroken ? (
                  <img src={`${API}/files/${data.directory.image}`} alt={data.directory.business_name} className="max-h-[70%] max-w-[80%] object-contain"
                    onLoad={(e) => { if (e.target.naturalWidth < 10) setLogoBroken(true); }}
                    onError={() => setLogoBroken(true)}
                    data-testid="preview-directory-logo" />
                ) : (
                  <span className="font-serif tracking-tight text-2xl text-ink/70 text-center leading-tight uppercase">{data.directory.business_name}</span>
                )}
              </div>
              <div className="p-4">
                <p className="overline text-[10px] text-gloss-gold-dark">{data.directory.category || "Member"}{data.directory.borough ? ` · ${data.directory.borough}` : ""}</p>
                <h3 className="font-serif text-xl text-ink mt-1">{data.directory.business_name}</h3>
                {data.directory.strapline && <p className="font-sans text-sm text-ink/55 mt-1">{data.directory.strapline}</p>}
              </div>
            </div>
          </div>
        )}

        {data.notice && (
          <div data-testid="preview-notice">
            <p className="overline text-ink/50 mb-4">Your Noticeboard post{data.notice.status !== "published" ? " — not yet live" : ""}</p>
            <div className="border-2 border-ink bg-white p-6 flex flex-col sm:flex-row gap-5">
              {data.notice.poster_image && <img src={`${API}/files/${data.notice.poster_image}`} alt="notice" className="w-full sm:w-56 object-contain self-start" />}
              <div>
                <h3 className="font-serif text-2xl text-ink">{data.notice.title}</h3>
                {data.notice.body && <p className="font-sans text-sm text-ink/60 mt-2 whitespace-pre-line">{data.notice.body}</p>}
                {data.notice.contact && <p className="font-mono text-[11px] text-ink/50 mt-3">{data.notice.contact}</p>}
              </div>
            </div>
          </div>
        )}

        {data.socials && Object.keys(data.socials).length > 0 && (
          <div data-testid="preview-socials">
            <p className="overline text-ink/50 mb-4">How readers reach you — printed with every feature</p>
            <div className="border border-ink/15 bg-white p-5 flex flex-wrap gap-x-8 gap-y-2">
              {Object.entries(data.socials).map(([k, v]) => (
                <p key={k} className="font-sans text-sm text-ink/70"><span className="font-mono text-[10px] uppercase tracking-widest text-ink/40 mr-2">{k}</span>{v}</p>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-ink/15 pt-8 space-y-4" data-testid="preview-signoff">
          {decision || data.member.preview_response ? (
            <div className="text-center space-y-2" data-testid="signoff-done">
              <p className="font-serif text-2xl text-ink">
                {(decision || data.member.preview_response?.decision) === "approved" ? "Wonderful — you've signed it off." : "Noted — we're on it."}
              </p>
              <p className="font-sans text-sm text-ink/55">
                {(decision || data.member.preview_response?.decision) === "approved"
                  ? "The desk has been told. Your features will go live when the editor schedules them — nothing publishes without the editor's say-so."
                  : "Your notes have landed on the editor's desk — we'll polish and send you a fresh preview."}
              </p>
              <button onClick={() => { setDecision(null); setShowNote(false); setData({ ...data, member: { ...data.member, preview_response: null } }); }}
                className="font-mono text-[11px] underline text-ink/50 hover:text-ink" data-testid="signoff-change-mind">Changed your mind? Respond again</button>
            </div>
          ) : (
            <>
              <p className="font-serif text-2xl text-ink text-center">Happy with everything?</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => respond("approved")} disabled={sending}
                  className="bg-ink text-cream px-8 py-4 font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="signoff-approve">
                  <Check size={14} /> Approve — sign it off
                </button>
                <button onClick={() => setShowNote(!showNote)} disabled={sending}
                  className="border-2 border-ink text-ink px-8 py-4 font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="signoff-changes">
                  <PenLine size={14} /> Request changes
                </button>
              </div>
              {showNote && (
                <div className="max-w-xl mx-auto space-y-3" data-testid="signoff-note-box">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4}
                    placeholder="Tell us what to change — a photo, a line of copy, a price, anything…"
                    className="w-full border-2 border-ink/30 p-4 font-sans text-sm focus:outline-none focus:border-ink" data-testid="signoff-note" />
                  <button onClick={() => respond("changes")} disabled={sending || !note.trim()}
                    className="w-full bg-ink text-cream px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="signoff-send-changes">
                    Send to the desk
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
